/**
 * Typed constants for all PostHog custom event names.
 *
 * Usage:
 *   import posthog from 'posthog-js';
 *   import { AnalyticsEvent } from '@/shared/lib/analytics/events';
 *   posthog.capture(AnalyticsEvent.AUTH_LOGIN_COMPLETED, { method: 'email' });
 */
export const AnalyticsEvent = Object.freeze({
  // ── P0: Auth & Onboarding ──
  AUTH_LOGIN_COMPLETED: 'auth_login_completed',
  AUTH_REGISTER_COMPLETED: 'auth_register_completed',
  ONBOARDING_PROFILE_STEP_COMPLETED: 'onboarding_profile_step_completed',
  ONBOARDING_PROFILE_COMPLETED: 'onboarding_profile_completed',
  // Marketing-consent capture on `/account/complete` (FRONT-426). taboo-lp owns
  // the region rule and the Klaviyo write; these record what we showed and what
  // came back. Neither belongs in the B1 whitelist — they are not funnel events
  // and the CDP budget is shared.
  // Properties: { variant: 'notice' | 'checkbox' | 'none' }.
  ONBOARDING_CONSENT_SHOWN: 'onboarding_consent_shown',
  // Server-side. Properties: { variant, consent, outcome, consent_method?, error_code? }.
  ONBOARDING_CONSENT_SUBMITTED: 'onboarding_consent_submitted',

  // ── P0: Subscription Funnel ──
  SUBSCRIPTION_PLAN_VIEWED: 'subscription_plan_viewed',
  SUBSCRIPTION_PLAN_SELECTED: 'subscription_plan_selected',
  SUBSCRIPTION_CHECKOUT_STARTED: 'subscription_checkout_started',
  // Funnel terminal event, sent with `send_instantly: true`. Two web shapes:
  // Whop checkout { email, method: 'whop_checkout', plan, ref, whop_plan_id }
  // and redeem-code grant { email, method: 'redeem_code', code?, plan: null, ref }.
  // The mobile app emits the same name with no `method` — a paid web conversion
  // is `method = 'whop_checkout'`. Coverage gap: it only fires when the payer
  // returns to /choose-plan and the status poll succeeds, so Whop payers who
  // never come back are missed (needs a BE Whop webhook).
  SUBSCRIPTION_ACTIVATED: 'subscription_activated',
  SUBSCRIPTION_WHOP_CALLBACK_FAILED: 'subscription_whop_callback_failed',
  SUBSCRIPTION_BLOCKED_EXISTING_USER: 'subscription_blocked_existing_user',
  SUBSCRIPTION_REDEEM_CODE_SUBMITTED: 'subscription_redeem_code_submitted',
  SUBSCRIPTION_REDEEM_CODE_APPLIED: 'subscription_redeem_code_applied',
  // Pro (tier 2) upgrade entry points: the 4K lock in the player, My
  // Subscription, the Pro card on /choose-plan. Properties: { source:
  // '4k_lock' | 'my_subscription' | 'choose_plan', target_tier: 2,
  // current_tier: number | null, quality? (4k_lock) }.
  SUBSCRIPTION_UPGRADE_CTA_CLICKED: 'subscription_upgrade_cta_clicked',

  // ── P0: Content Consumption ──
  VIDEO_PLAYED: 'video_played',
  VIDEO_PAUSED: 'video_paused',
  SHORT_VIEWED: 'short_viewed',
  SHORT_LIKED: 'short_liked',
  SHORT_GLOBE_OPENED: 'short_globe_opened',
  // Globe embed couldn't resolve the focused uuid (deleted or mature-filtered
  // short) — inbound `focus_failed` postMessage. Tracks dead-pin rate.
  SHORT_GLOBE_FOCUS_FAILED: 'short_globe_focus_failed',
  VIDEO_GLOBE_OPENED: 'video_globe_opened',
  // Subscribe CTA click on the Taboo Terra 402 quota wall (IA-41 gap closure).
  // Props: video_uuid, entry_point. Chat-engine events themselves are BE-emitted
  // by taboo-rag — this is the FE-side conversion intent only.
  AI_QUOTA_CTA_CLICKED: 'ai_quota_cta_clicked',
  // Click on a Taboo Terra entry (opens /ai). Props: entry_point ('header'),
  // has_new_dot, from_path. New entries pass their own entry_point — no new names.
  AI_ENTRY_CLICKED: 'ai_entry_clicked',

  // ── P0: Engagement ──
  VIDEO_LIKED: 'video_liked',
  // Card hover Share (anonymous viewers see Share where authed see Save).
  // Props: video_uuid, share_method ('native_share' | 'clipboard'), source, is_authenticated.
  VIDEO_SHARED: 'video_shared',
  WATCHLIST_ITEM_ADDED: 'watchlist_item_added',
  COMMENT_CREATED: 'comment_created',
  CREATOR_FOLLOWED: 'creator_followed',

  // ── P0: Search ──
  SEARCH_PERFORMED: 'search_performed',
  SEARCH_FILTER_CHANGED: 'search_filter_changed',
  SEARCH_SORT_CHANGED: 'search_sort_changed',

  // ── P0: Payment-First Onboarding ──
  AUTH_LEAD_CAPTURED: 'auth_lead_captured',
  AUTH_POST_CHECKOUT_ACCOUNT_CREATED: 'auth_post_checkout_account_created',
  AUTH_FP_REFERRAL_TRACKED: 'auth_fp_referral_tracked',
  SUBSCRIPTION_GUEST_CHECKOUT_STARTED: 'subscription_guest_checkout_started', // docs: "embedded checkout"

  // ── P1: Auth ──
  AUTH_LOGIN_FAILED: 'auth_login_failed',
  AUTH_REGISTER_FAILED: 'auth_register_failed',
  AUTH_LOGOUT_COMPLETED: 'auth_logout_completed',
  AUTH_PASSWORD_RESET_REQUESTED: 'auth_password_reset_requested',
  AUTH_PASSWORD_RESET_COMPLETED: 'auth_password_reset_completed',
  AUTH_PASSWORD_RESET_FAILED: 'auth_password_reset_failed',
  AUTH_OTP_SENT: 'auth_otp_sent',
  AUTH_OTP_VERIFIED: 'auth_otp_verified',
  AUTH_OTP_BLOCKED_NO_SUBSCRIPTION: 'auth_otp_blocked_no_subscription',
  AUTH_SIGN_IN_SWITCHED_TO_OTP: 'auth_sign_in_switched_to_otp',
  AUTH_SIGN_IN_SWITCHED_TO_PASSWORD: 'auth_sign_in_switched_to_password',
  AUTH_DISCORD_REDIRECT_INITIATED: 'auth_discord_redirect_initiated',
  AUTH_DISCORD_OAUTH_CANCELED: 'auth_discord_oauth_canceled',
  AUTH_DISCORD_OPTIONS_VIEWED: 'auth_discord_options_viewed',
  AUTH_DISCORD_OPTION_CHOSEN: 'auth_discord_option_chosen',
  AUTH_DISCORD_LINK_OTP_SENT: 'auth_discord_link_otp_sent',
  // 2FA login challenge (FRONT-431). `method` = the entry that was challenged;
  // `factor` = 'totp' | 'recovery'; failures carry the BE `error_code`.
  AUTH_2FA_CHALLENGE_SHOWN: 'auth_2fa_challenge_shown',
  AUTH_2FA_VERIFIED: 'auth_2fa_verified',
  AUTH_2FA_VERIFY_FAILED: 'auth_2fa_verify_failed',
  // 2FA settings (FRONT-430). `factor` on disable = 'totp' | 'recovery'.
  AUTH_2FA_ENABLED: 'auth_2fa_enabled',
  AUTH_2FA_DISABLED: 'auth_2fa_disabled',
  AUTH_2FA_RECOVERY_CODES_REGENERATED: 'auth_2fa_recovery_codes_regenerated',

  // ── P1: Video Player ──
  VIDEO_SEEKED: 'video_seeked',
  VIDEO_COMPLETED: 'video_completed',
  VIDEO_QUALITY_CHANGED: 'video_quality_changed',
  VIDEO_WATCH_MILESTONE: 'video_watch_milestone',
  // Arrival via a Taboo AI / admin deep-link (`?t=<sec>`) — FRONT-186.
  VIDEO_TIMESTAMP_JUMP_ARRIVED: 'video_timestamp_jump_arrived',

  // ── P1: Series ──
  // Fire from the series player AND the /videos/[id] watch page (FRONT-377);
  // `surface: 'series_player' | 'watch'` tells them apart.
  SERIES_EPISODE_STARTED: 'series_episode_started',
  SERIES_EPISODE_COMPLETED: 'series_episode_completed',
  SERIES_INFO_OPENED: 'series_info_opened',
  SERIES_INFO_PLAY_CLICKED: 'series_info_play_clicked',
  // Season chip on the series detail page (FRONT-380). Props: series_id,
  // season_uuid (null = Extras), season_index, source ('detail').
  SERIES_SEASON_SWITCHED: 'series_season_switched',

  // ── P1: Engagement ──
  COMMENT_REPLY_CREATED: 'comment_reply_created',
  SHORT_SHARED: 'short_shared',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',
  SUBSCRIPTION_MANAGE_CLICKED: 'subscription_manage_clicked',

  // ── P1: Creator Studio ──
  // Fires from BOTH publish paths (FRONT-354): the upload-modal button
  // (`source: 'modal'`) and the content-list visibility menu (`source: 'list'`,
  // after the schedule mutation resolves). Always carries `video_uuid` so it
  // joins studio_video_ready — the only way to count ready-never-published.
  STUDIO_CONTENT_PUBLISHED: 'studio_content_published',
  // The schedule the modal publish path actually sent, fired after POST
  // /schedule resolves. `studio_content_published` records the creator's
  // intent; this records what the BE received, so a dropped date shows up as
  // `schedule_origin: 'publish_now'` against an intent of `scheduled`.
  STUDIO_PUBLISH_SCHEDULED: 'studio_publish_scheduled',
  STUDIO_CODE_CREATED: 'studio_code_created',
  STUDIO_CODE_SHARED: 'studio_code_shared',
  STUDIO_CODE_DEACTIVATED: 'studio_code_deactivated',
  STUDIO_CODE_REACTIVATED: 'studio_code_reactivated',
  STUDIO_CODE_UPDATED: 'studio_code_updated',
  // Profile-tabs reorder (FRONT-274 system markers). Props: materialized
  // (first reorder — type_marker items in payload), custom_count,
  // interleaved (creator mixed defaults among customs vs defaults-first).
  STUDIO_TABS_REORDERED: 'studio_tabs_reordered',
  // Creator mutation success side (FRONT-345). Every surface below already
  // had captureError on failure; these close the pair so a funnel per creator
  // journey is computable. Fire right after the awaited mutation resolves.
  STUDIO_COURSE_CREATED: 'studio_course_created',
  STUDIO_COURSE_UPDATED: 'studio_course_updated',
  STUDIO_COURSE_SUBMITTED_FOR_REVIEW: 'studio_course_submitted_for_review',
  STUDIO_COURSE_SUBMISSION_WITHDRAWN: 'studio_course_submission_withdrawn',
  STUDIO_COURSE_UNPUBLISHED: 'studio_course_unpublished',
  // Course resources — creator write side of the attachments the learner sees
  // on /courses/[identifier] (`course_resource_*` events are the read side).
  // Props on all four: course_uuid, scope ('course' | 'lesson'), video_id.
  // Uploaded/deleted also carry attachment_uuid, mime_type, extension,
  // size_bytes. The failure event carries error_code + error_message per the
  // FRONT-341 studio convention (max_files | invalid_type | too_large |
  // http_<status> | network).
  STUDIO_COURSE_RESOURCE_UPLOADED: 'studio_course_resource_uploaded',
  STUDIO_COURSE_RESOURCE_UPLOAD_FAILED: 'studio_course_resource_upload_failed',
  STUDIO_COURSE_RESOURCE_UPDATED: 'studio_course_resource_updated',
  STUDIO_COURSE_RESOURCE_DELETED: 'studio_course_resource_deleted',
  // Series (creator playlists, FRONT-376). No review states — publish is
  // creator-callable, so the pair is published/unpublished, plus delete
  // (courses have no delete). Props: series_uuid, episodes_count.
  STUDIO_SERIES_CREATED: 'studio_series_created',
  STUDIO_SERIES_UPDATED: 'studio_series_updated',
  STUDIO_SERIES_PUBLISHED: 'studio_series_published',
  STUDIO_SERIES_UNPUBLISHED: 'studio_series_unpublished',
  STUDIO_SERIES_DELETED: 'studio_series_deleted',
  STUDIO_TAB_CREATED: 'studio_tab_created',
  STUDIO_TAB_UPDATED: 'studio_tab_updated',
  STUDIO_TAB_DELETED: 'studio_tab_deleted',
  STUDIO_TAB_VIDEO_PINNED: 'studio_tab_video_pinned',
  STUDIO_TAB_VIDEO_UNPINNED: 'studio_tab_video_unpinned',
  STUDIO_KNOWLEDGE_DRAFT_SAVED: 'studio_knowledge_draft_saved',
  STUDIO_KNOWLEDGE_SUBMITTED: 'studio_knowledge_submitted',
  STUDIO_KNOWLEDGE_DELETED: 'studio_knowledge_deleted',
  // Pre-launch studio pages (FRONT-349). Fire when the page's primary query
  // settles (data, or the structural NO_PROMOTER_ACCOUNT answer) — not on
  // mount — so `has_promoter` is known. $pageview already counts visits;
  // these carry the page state.
  STUDIO_DASHBOARD_VIEWED: 'studio_dashboard_viewed',
  STUDIO_ANALYTICS_VIEWED: 'studio_analytics_viewed',
  STUDIO_PAYOUTS_VIEWED: 'studio_payouts_viewed',

  // ── Coins / Paid DMs (FRONT-313) ──
  // Wallet surface (live since FRONT-309). Props: balance (integer).
  COINS_WALLET_VIEWED: 'coins_wallet_viewed',
  // Empty-state CTA → /creators. Props: balance (integer).
  COINS_WALLET_FIND_CREATORS_CLICKED: 'coins_wallet_find_creators_clicked',
  // Pre-registered — capture sites land with their surfaces (success+failure
  // pairs, never one-sided counters):
  // FRONT-310 send flow. Props: channel_uuid, coins_charged, thread_new, deduplicated.
  DM_MESSAGE_SENT: 'dm_message_sent',
  // Props: channel_uuid, status, error_field ('balance'|'channel'|'client_id'|null).
  DM_MESSAGE_SEND_FAILED: 'dm_message_send_failed',
  // FRONT-311/312 thread views. Props: perspective ('subscriber'|'creator'), unread_count.
  DM_THREAD_OPENED: 'dm_thread_opened',
  // FRONT-312 studio reply. Props: thread_uuid (+ status on failure).
  DM_REPLY_SENT: 'dm_reply_sent',
  DM_REPLY_SEND_FAILED: 'dm_reply_send_failed',

  // ── P2: Toggle / Undo Events ──
  VIDEO_LIKE_REMOVED: 'video_like_removed',
  SHORT_LIKE_REMOVED: 'short_like_removed',
  WATCHLIST_ITEM_REMOVED: 'watchlist_item_removed',
  COMMENT_DELETED: 'comment_deleted',
  CREATOR_UNFOLLOWED: 'creator_unfollowed',

  // ── P2: Player Controls ──
  VIDEO_FULLSCREEN_TOGGLED: 'video_fullscreen_toggled',
  VIDEO_AUTOPLAY_TOGGLED: 'video_autoplay_toggled',
  VIDEO_SPEED_CHANGED: 'video_speed_changed',

  // ── Instant playback (related-click swap) ──
  // Pair: `initiated` fires at the click that writes the optimistic store;
  // `first_frame` fires at the first `play` event after the new src starts.
  // Together they measure perceived swap latency (the YouTube-instant win)
  // and surface bad payloads (had_hls_url=false ⇒ store wrote a video with
  // no playable URL, would otherwise hang silently).
  VIDEO_SWAP_INITIATED: 'video_swap_initiated',
  VIDEO_SWAP_FIRST_FRAME: 'video_swap_first_frame',

  // ── P2: Series ──
  SERIES_UP_NEXT_TRIGGERED: 'series_up_next_triggered',
  SERIES_UP_NEXT_CANCELLED: 'series_up_next_cancelled',
  // Loop / shuffle toggles in the series player sidebar (FRONT-377).
  // Props: series_id, mode ('normal' | 'repeat' | 'shuffle'). 'repeat' = current episode
  // (2026-10-05, FRONT-483); rows before that carry 'loop' = loop the series.
  SERIES_PLAYBACK_MODE_CHANGED: 'series_playback_mode_changed',

  // ── P2: Invite System ──
  INVITE_CODE_GENERATED: 'invite_code_generated',
  INVITE_CODE_SHARED: 'invite_code_shared',

  // ── P2: Studio ──
  STUDIO_UPLOAD_STARTED: 'studio_upload_started',
  // Non-blocking file sanity finding (FRONT-348). One event per finding:
  // `check` ∈ empty_file | not_a_video | renamed_container |
  // browser_cannot_decode | duration_unknown | very_short, `severity` ∈
  // error | warning | info. Measures the checks' hit-rate; never gates.
  STUDIO_UPLOAD_PRECHECK_FLAGGED: 'studio_upload_precheck_flagged',
  STUDIO_UPLOAD_PROCESSING_FAILED: 'studio_upload_processing_failed',
  STUDIO_UPLOAD_POLL_FAILED: 'studio_upload_poll_failed',
  STUDIO_CONTENT_DELETED: 'studio_content_deleted',
  STUDIO_VISIBILITY_CHANGED: 'studio_visibility_changed',
  STUDIO_UPLOAD_RESUME_ATTEMPTED: 'studio_upload_resume_attempted',
  // The creator pressed "Retry upload" on an errored upload in the modal.
  // Distinct from RESUME_ATTEMPTED, which restarts a persisted row from the
  // content table. Until FRONT-438 this reached only the local
  // `upload-history-store` (`retryCount` in the Upload health panel), so how
  // often a failure is recoverable by simply trying again was answerable on one
  // creator's machine and nowhere else. Carries the `previous_error_code` /
  // `previous_failure_reason` it is retrying, from the same
  // `deriveUploadState` catalog that feeds `failure_reason`.
  STUDIO_UPLOAD_RETRY_ATTEMPTED: 'studio_upload_retry_attempted',
  STUDIO_UPLOAD_RESUME_VALIDATION_FAILED: 'studio_upload_resume_validation_failed',
  STUDIO_UPLOAD_RESUME_SUCCEEDED: 'studio_upload_resume_succeeded',
  STUDIO_UPLOAD_RESUME_FAILED: 'studio_upload_resume_failed',
  STUDIO_UPLOAD_PREPARED: 'studio_upload_prepared',
  STUDIO_UPLOAD_PREPARE_FAILED: 'studio_upload_prepare_failed',
  STUDIO_UPLOAD_TUS_COMPLETED: 'studio_upload_tus_completed',
  STUDIO_UPLOAD_TUS_FAILED: 'studio_upload_tus_failed',
  STUDIO_UPLOAD_RECONNECTING: 'studio_upload_reconnecting',
  // Bunny checks AuthorizationExpire on upload creation (POST), never on chunk
  // writes (PATCH), so an upload can stream for hours past expiry and then die
  // on the next create. Fires when we re-mint the signature mid-upload to stop
  // that. `expired: true` means we were already past the deadline — the shape
  // that killed four uploads on 2026-09-07.
  STUDIO_UPLOAD_SIGNATURE_REFRESHED: 'studio_upload_signature_refreshed',
  // The re-mint came back against a DIFFERENT bunny_video_id, so we threw it
  // away rather than restart the upload at byte 0 against a new Bunny record.
  // The discard was a bare `return` until FRONT-438: the upload then died on a
  // Bunny 400 that looked exactly like an ordinary expiry. `error_code` is
  // always `bunny_video_id_mismatch`; it carries one because a BE minting
  // against the wrong video is a contract break, not a network blip.
  STUDIO_UPLOAD_SIGNATURE_REFRESH_REJECTED: 'studio_upload_signature_refresh_rejected',
  // Fires when an upload is stopped because nothing durably committed for
  // NO_COMMIT_TIMEOUT_MS. Distinct from a slow upload, which is allowed to run
  // as long as it needs: this one had bytes moving and an offset that never
  // advanced. Pairs with `chunk_bytes` to show how far the sizer had backed off.
  STUDIO_UPLOAD_NO_COMMIT_TIMEOUT: 'studio_upload_no_commit_timeout',
  // The OTHER stop, and the reason this event exists: `scheduleStaleCheck`
  // aborts an upload after STALE_PROGRESS_TIMEOUT_MS (30 min) without a single
  // progress tick, which is four times sooner than the documented no-commit
  // rule (2h) and had only a console.warn behind it. Both are silence rules
  // rather than speed rules, but they measure different silences — ticks vs
  // durable commits — and until this fires we cannot say which one is actually
  // ending hostile-network uploads. Compare its rate against
  // `studio_upload_no_commit_timeout` before touching either threshold.
  STUDIO_UPLOAD_STALLED: 'studio_upload_stalled',
  // The wait AFTER the last byte lands, which is what creators actually call
  // "processing".
  //
  // Read this as OUR VISIBILITY of Bunny, not as Bunny's work. `bunny_status`
  // is a BE column, not a live proxy — a stall here means the column did not
  // advance, which is not the same as Bunny being slow.
  //
  // `bunny_status_source` (BACK-209) is what separates the two, so read it
  // before blaming anything: 'reconciler' means Bunny's webhook had not landed
  // and the BE reaper pulled the status from their live API instead. That was
  // the 2026-09-08 root cause — Bunny's webhook stream ran 38 min behind their
  // own live API, and the reaper saw the newer state but did not sync it.
  //
  // Split by `stall_kind`:
  //   ingest — column still reads status 0/absent. The BE reaper now syncs
  //            in-flight progress every 5 min, so this firing at all means the
  //            reaper did not close the gap either.
  //   encode — column reads status >= 1 but not finished. Duration does not
  //            track file size, so pair with `total_bytes` before concluding.
  // Fires at most once per upload per kind. Until this existed the only way to
  // see either stall was to hand-query after a creator complained.
  STUDIO_UPLOAD_PROCESSING_STALLED: 'studio_upload_processing_stalled',
  // On-demand read-through to Bunny's live API (BE: BACK-209). `trigger`
  // separates the automatic escalation from the creator pressing the button.
  // `synced_now: true` means our column was stale and this call corrected it —
  // the rate of that is the honest measure of how far behind the webhook
  // stream is running.
  STUDIO_UPLOAD_STATUS_REFRESHED: 'studio_upload_status_refreshed',
  // Hit the BE's 6/min per-video guard. Should be near-zero: the client's own
  // escalation caps at 1/min per video, so volume here means the creator is
  // hammering the button, or something is retrying that should not be.
  STUDIO_UPLOAD_STATUS_REFRESH_THROTTLED: 'studio_upload_status_refresh_throttled',
  // The refresh call itself failed for a reason that is not the 6/min guard.
  // The content-row button previously swallowed these into a toast plus a
  // warning-severity captureError, so "creators press Check status and nothing
  // happens" could not be answered from data. Carries error_code +
  // error_message per the studio failure convention.
  STUDIO_UPLOAD_STATUS_REFRESH_FAILED: 'studio_upload_status_refresh_failed',
  // The client has stopped asking Bunny about this video and handed it to the
  // BE reaper. Escalation ends at reaper parity, which lands after the third
  // refresh — so a video that is still encoding then goes telemetry-silent
  // until it finishes. On 2026-09-15 video `5e4ccb65` that silence ran 24
  // minutes, and nothing in the data distinguished it from a video nobody was
  // watching. Carries `waiting_ms` and the status provenance at hand-off.
  STUDIO_UPLOAD_REFRESH_EXHAUSTED: 'studio_upload_refresh_exhausted',
  // Fires once per upload when observed throughput drops below the slow
  // threshold (see SLOW_UPLOAD_MBPS_THRESHOLD) — measures how many creators
  // hit the bad-route-to-Bunny-ingest class of problem (2026-08-13 incident).
  STUDIO_UPLOAD_SLOW_CONNECTION: 'studio_upload_slow_connection',
  STUDIO_ENCODING_STARTED: 'studio_encoding_started',
  STUDIO_VIDEO_READY: 'studio_video_ready',
  STUDIO_UPLOAD_ABANDONED: 'studio_upload_abandoned',
  // Upload modal lifecycle. `/studio/content` renders the modal unconditionally,
  // so it never unmounts and "is it open" shows up in neither the URL nor any
  // other event — a creator watching progress in the modal and one sitting on
  // the content page look identical in the data. Carries `mode` and the
  // `upload_phase` in flight at the moment of the toggle, which is the half
  // that makes a close worth reading.
  STUDIO_UPLOAD_MODAL_OPENED: 'studio_upload_modal_opened',
  STUDIO_UPLOAD_MODAL_CLOSED: 'studio_upload_modal_closed',
  // Upload health panel (FRONT-351) — the creator's local attempt history
  // (`upload-history-store`). `viewed` on every open with `source`
  // (`help` = Help panel article, `row` = Details on a failed row) + row counts;
  // `report_copied` when a creator copies a per-attempt report for support —
  // the self-serve signal: rising copies with `failure_reason` set means
  // creators are diagnosing without an engineer in the loop.
  STUDIO_UPLOAD_HEALTH_VIEWED: 'studio_upload_health_viewed',
  STUDIO_UPLOAD_HEALTH_REPORT_COPIED: 'studio_upload_health_report_copied',
  // Import-from-link funnel (FRONT-341), additive to the shared
  // `source: 'url_fetch'` tagging above. `started` on dispatch, then exactly
  // one terminal: `rejected` when the BE resolver answered a structured 422
  // (`error_code` ∈ SOURCE_IS_FRAMEIO_SHARE | SOURCE_IS_DRIVE_PAGE |
  // SOURCE_NOT_A_VIDEO — creator pasted a share page, not an outage),
  // `completed` when the poller sees a playable bunny_status, `failed` for
  // everything else with `step` (dispatch | encoding | poll) and a derived
  // `error_code` (http_<status> | network | bunny_<status> | poll_lost).
  STUDIO_URL_FETCH_STARTED: 'studio_url_fetch_started',
  STUDIO_URL_FETCH_REJECTED: 'studio_url_fetch_rejected',
  STUDIO_URL_FETCH_COMPLETED: 'studio_url_fetch_completed',
  STUDIO_URL_FETCH_FAILED: 'studio_url_fetch_failed',
  // BE-emitted (tabootv_bo reaper → /api/internal/studio-reaper-run and
  // /studio-video-reconciled). Per-run liveness + per-row terminal outcome for
  // stuck Bunny rows — the only closure for dashboard tiles #11/#12. Absence of
  // studio_reaper_run for >30 min means the scheduler is down (FRONT-346).
  STUDIO_REAPER_RUN: 'studio_reaper_run',
  STUDIO_VIDEO_RECONCILED: 'studio_video_reconciled',

  // ── Creator Agreement gate (FRONT-342) ──
  // The dialog gates every creator mutation (upload, resume, visibility,
  // codes, community post) and auto-opens on first login. `source` names the
  // entry point — viewed-without-accept from `first_login` is expected;
  // from `studio_*` it's friction; any `failed` is an outage.
  CREATOR_CONTRACT_VIEWED: 'creator_contract_viewed',
  CREATOR_CONTRACT_ACCEPTED: 'creator_contract_accepted',
  CREATOR_CONTRACT_DISMISSED: 'creator_contract_dismissed',
  CREATOR_CONTRACT_FAILED: 'creator_contract_failed',

  // ── Creator application (/become-creator self-serve onboarding) ──
  // `_failed` carries `error_code` (`http_<status>` | `network`); 409/422/429
  // are contract answers the form handles, so only 5xx/network is an outage.
  CREATOR_APPLICATION_SUBMITTED: 'creator_application_submitted',
  CREATOR_APPLICATION_FAILED: 'creator_application_failed',

  // ── P1: Subscription Dunning ──
  // Fires once per Whop `payment.failed` webhook delivery. Each retry attempt
  // is its own event — does NOT flip `is_subscribed` (final-state lives on
  // Laravel's `membership.went_invalid`). Trigger for the Klaviyo dunning
  // flow (E2 in docs/guides/klaviyo-marketing.md §6). See route at
  // src/app/api/webhooks/whop/payment-failed/route.ts.
  SUBSCRIPTION_PAYMENT_FAILED: 'subscription_payment_failed',

  // ── P1: Subscription Cancellation ──
  SUBSCRIPTION_CANCEL_STARTED: 'subscription_cancel_started',
  SUBSCRIPTION_CANCEL_REASON_SELECTED: 'subscription_cancel_reason_selected',
  SUBSCRIPTION_RETENTION_OFFER_ACCEPTED: 'subscription_retention_offer_accepted',
  SUBSCRIPTION_CANCEL_CONFIRMED: 'subscription_cancel_confirmed',
  SUBSCRIPTION_CANCEL_COMPLETED: 'subscription_cancel_completed',
  SUBSCRIPTION_CANCEL_FAILED: 'subscription_cancel_failed',
  SUBSCRIPTION_CANCEL_ABANDONED: 'subscription_cancel_abandoned',

  // ── P1: Subscription Discount (free-days) Offer ──
  SUBSCRIPTION_DISCOUNT_OFFERED: 'subscription_discount_offered',
  SUBSCRIPTION_DISCOUNT_ACCEPTED: 'subscription_discount_accepted',
  SUBSCRIPTION_DISCOUNT_FAILED: 'subscription_discount_failed',

  // ── P1: Subscription Compare / Support Offer Impressions ──
  SUBSCRIPTION_COMPARE_OFFERED: 'subscription_compare_offered',
  SUBSCRIPTION_SUPPORT_OFFERED: 'subscription_support_offered',
  SUBSCRIPTION_RETENTION_OFFER_DECLINED: 'subscription_retention_offer_declined',

  // ── P1: Subscription Pause / Resume ──
  SUBSCRIPTION_PAUSE_OFFERED: 'subscription_pause_offered',
  SUBSCRIPTION_PAUSE_DURATION_SELECTED: 'subscription_pause_duration_selected',
  SUBSCRIPTION_PAUSE_CONFIRMED: 'subscription_pause_confirmed',
  SUBSCRIPTION_PAUSE_COMPLETED: 'subscription_pause_completed',
  SUBSCRIPTION_PAUSE_FAILED: 'subscription_pause_failed',
  SUBSCRIPTION_RESUME_CLICKED: 'subscription_resume_clicked',
  SUBSCRIPTION_RESUME_COMPLETED: 'subscription_resume_completed',
  SUBSCRIPTION_RESUME_FAILED: 'subscription_resume_failed',
  SUBSCRIPTION_PAUSE_SUPPORT_CLICKED: 'subscription_pause_support_clicked',
  SUBSCRIPTION_PAUSE_GATE_VIEWED: 'subscription_pause_gate_viewed',

  // ── P2: Community & Profile ──
  POST_CREATED: 'post_created',
  NOTIFICATION_CLICKED: 'notification_clicked',
  PROFILE_UPDATED: 'profile_updated',
  PASSWORD_CHANGED: 'password_changed',

  // ── P1: Clip Creator ──
  CLIP_PAGE_VIEWED: 'clip_page_viewed',
  CLIP_GENERATION_STARTED: 'clip_generation_started',
  CLIP_GENERATION_COMPLETED: 'clip_generation_completed',
  CLIP_GENERATION_FAILED: 'clip_generation_failed',
  CLIP_GENERATION_CANCELLED: 'clip_generation_cancelled',
  CLIP_DOWNLOADED: 'clip_downloaded',
  CLIP_MOMENT_SELECTED: 'clip_moment_selected',
  CLIP_RESOLUTION_CHANGED: 'clip_resolution_changed',
  CLIP_TIMELINE_ADJUSTED: 'clip_timeline_adjusted',
  CLIP_FULL_VIDEO_DOWNLOADED: 'clip_full_video_downloaded',

  // ── Share Clips (in-app /s/{slug} references — distinct from Clip Creator downloads) ──
  SHARE_CLIP_CREATED: 'share_clip_created',
  SHARE_CLIP_CREATE_FAILED: 'share_clip_create_failed',
  SHARE_CLIP_LINK_COPIED: 'share_clip_link_copied',
  SHARE_CLIP_PAGE_VIEWED: 'share_clip_page_viewed',
  SHARE_CLIP_FULL_VIDEO_CLICKED: 'share_clip_full_video_clicked',

  // ── Course Funnel ──
  COURSE_VIEWED: 'course_viewed',
  COURSE_CHECKOUT_STARTED: 'course_checkout_started',
  COURSE_PURCHASE_COMPLETED: 'course_purchase_completed',
  COURSE_BLOCKED_EXISTING_USER: 'course_blocked_existing_user',
  COURSE_POST_CHECKOUT_FAILED: 'course_post_checkout_failed',
  COURSE_POST_CHECKOUT_RETRIED: 'course_post_checkout_retried',
  // Curriculum accordion (FRONT-379). User toggles only — never the initial
  // auto-open. Props: course_id, module_uuid (null = Main lessons),
  // module_index, open, source ('detail' | 'player').
  COURSE_MODULE_TOGGLED: 'course_module_toggled',
  COURSE_RESOURCES_VIEWED: 'course_resources_viewed',
  COURSE_RESOURCE_DOWNLOADED: 'course_resource_downloaded',

  // ── Freemium Funnel ──
  FREEMIUM_CATALOG_VIEWED: 'freemium_catalog_viewed',
  FREEMIUM_CHANNEL_VIEWED: 'freemium_channel_viewed',
  FREEMIUM_VIDEO_PLAYED: 'freemium_video_played',
  FREEMIUM_VIDEO_COMPLETED: 'freemium_video_completed',
  FREEMIUM_GATE_TRIGGERED: 'freemium_gate_triggered',
  FREEMIUM_CONVERSION_CARD_SHOWN: 'freemium_conversion_card_shown',
  // Guest conversion CTA → plan page. Props: source, destination, handler
  // (creator CTAs only). Sources: gate_<type>, end_card, and the creator
  // channel unlock CTAs — top_header, creator_header, featured_video,
  // ticket_band, mini_passport (FRONT-392).
  FREEMIUM_CONVERSION_CTA_CLICKED: 'freemium_conversion_cta_clicked',
  FREEMIUM_BANNER_DISMISSED: 'freemium_banner_dismissed',

  // ── Checkout diagnostics (cross-funnel: subscription + course) ──
  CHECKOUT_COMPLETED: 'checkout_completed',
  CHECKOUT_EMAIL_MISMATCH: 'checkout_email_mismatch',
  CHECKOUT_PAYMENT_ENRICHED: 'checkout_payment_enriched',
  // Whop Elements checkout widget load outcome, once per mount (FRONT-504) —
  // the high-volume signal that checkout itself is broken. `load_ms` from mount;
  // failed carries `error_code` ('timeout' | 'sdk_load_error' | Whop's code).
  CHECKOUT_WIDGET_LOADED: 'checkout_widget_loaded',
  CHECKOUT_WIDGET_LOAD_FAILED: 'checkout_widget_load_failed',

  // ── Error telemetry (fires from captureError + mutation error handlers) ──
  CLIENT_ERROR: 'client_error',
  FOLLOW_TOGGLE_ERROR: 'follow_toggle_error',

  // ── Auth UX ──
  AUTH_ALREADY_SIGNED_IN_TOAST_SHOWN: 'auth_already_signed_in_toast_shown',

  // ── Support chat (Crisp) — platform surface ──
  // LP mirrors with `landing_chat_*`. Same provider, separate namespace so
  // funnels can isolate platform-side conversations from anonymous lp ones.
  PLATFORM_CHAT_OPENED: 'platform_chat_opened',
  PLATFORM_CHAT_MESSAGE_SENT: 'platform_chat_message_sent',
  PLATFORM_CHAT_EMAIL_COLLECTED: 'platform_chat_email_collected',

  // ── Subscription verification timeout (Whop webhook stuck) ──
  SUBSCRIPTION_ACTIVATION_STUCK: 'subscription_activation_stuck',

  // ── Choose Plan: full-state screens (active subscription, account mismatch) ──
  SUBSCRIPTION_ACTIVE_SCREEN_VIEWED: 'subscription_active_screen_viewed',
  SUBSCRIPTION_ACTIVE_MANAGE_CLICKED: 'subscription_active_manage_clicked',
  SUBSCRIPTION_ACTIVE_BACK_CLICKED: 'subscription_active_back_clicked',
  SUBSCRIPTION_MISMATCH_SCREEN_VIEWED: 'subscription_mismatch_screen_viewed',
  SUBSCRIPTION_MISMATCH_LOGOUT_CLICKED: 'subscription_mismatch_logout_clicked',
  SUBSCRIPTION_MISMATCH_STAY_CLICKED: 'subscription_mismatch_stay_clicked',

  // ── Choose Plan: pay-method picker (Card vs Gift code) ──
  // Picker sits on `step='checkout'` between email submit and the embedded
  // Whop card form. Whop session is created on email submit (today's timing);
  // gift-code picks bypass that session — accept the wasted session as MVP.

  // ── Choose Plan: express wallet checkout (checkout_express_button experiment) ──
  // Fires once per express-button mount with what the device resolved to.
  // Props: rendered ('apple-pay' | 'google-pay' | 'whop-pay' | 'none'), plan,
  // is_authenticated. 'none' = wallet unavailable, button hidden (embed only).

  // Promo code applied/removed inside the Whop embed (frontend signal —
  // canonical source for billed amount remains Payment.promo_code on
  // payment.succeeded webhook). Captures attempts, including ones the user
  // removes before paying.

  // ── Admin / Impersonation ──
  ADMIN_IMPERSONATION_STARTED: 'admin_impersonation_started',
  ADMIN_IMPERSONATION_STOPPED: 'admin_impersonation_stopped',

  // ── App-Install Badge (mobile-web banner) ──
  APP_BADGE_SHOWN: 'app_badge_shown',
  APP_BADGE_DISMISSED: 'app_badge_dismissed',
  APP_BADGE_STORE_CLICKED: 'app_badge_store_clicked',

  // ── Location opt-in prompt (globe heatmap, FRONT-218) ──
  // Funnel: shown → allowed → shared (success). Drop-offs: dismissed (X/Not now),
  // permission_denied (browser block), share_failed (POST 422/429/network).
  LOCATION_PROMPT_SHOWN: 'location_prompt_shown',
  LOCATION_PROMPT_ALLOWED: 'location_prompt_allowed',
  LOCATION_PROMPT_DISMISSED: 'location_prompt_dismissed',
  LOCATION_SHARED: 'location_shared',
  LOCATION_PERMISSION_DENIED: 'location_permission_denied',
  LOCATION_SHARE_FAILED: 'location_share_failed',
  // Manual opt-out from the /account settings toggle.
  LOCATION_SHARING_DISABLED: 'location_sharing_disabled',

  // ── What's New (announcements popup + /whats-new page) ──
  // popup_shown { variant: 'single'|'tour', item_count, overflow_count, announcement_ids, pathname }
  // popup_dismissed { variant, method: 'close'|'confirm'|'skip'|'scrim'|'escape'|'done'|'history', step_index, pathname }
  // cta_clicked { announcement_id, redirect, variant: 'single'|'tour'|'page', step_index }
  // page_viewed { unread_count, total_count, filter }
  // fetch_failed / mark_read_failed { error_type, error_code: 'http_<status>'|'network' }
  WHATS_NEW_POPUP_SHOWN: 'whats_new_popup_shown',
  WHATS_NEW_POPUP_DISMISSED: 'whats_new_popup_dismissed',
  WHATS_NEW_CTA_CLICKED: 'whats_new_cta_clicked',
  WHATS_NEW_PAGE_VIEWED: 'whats_new_page_viewed',
  WHATS_NEW_FETCH_FAILED: 'whats_new_fetch_failed',
  WHATS_NEW_MARK_READ_FAILED: 'whats_new_mark_read_failed',

  // ── Feature tours (generic engine, every tour shares these) ──
  // started { tour_id, tour_version, total_steps, trigger: 'link'|'programmatic' }
  // step_viewed { tour_id, step_index, step_id, total_steps }
  // completed / skipped { tour_id, step_index, step_id, total_steps }
  TOUR_STARTED: 'tour_started',
  TOUR_STEP_VIEWED: 'tour_step_viewed',
  TOUR_COMPLETED: 'tour_completed',
  TOUR_SKIPPED: 'tour_skipped',

  // ── Redeem Code Flow ──
  // Creator/gift-code onboarding (separate from the paid subscription checkout).
  // Every event in this section MUST carry { flow: 'redeem', code_type: 'creator' | 'gift' }
  // so dashboards can isolate the funnel from `subscription_*` (paid) events.
  // Reference: docs/guides/posthog-analytics.md → "Redeem funnel".
  REDEEM_FLOW_STARTED: 'redeem_flow_started',
  REDEEM_CODE_SUBMITTED: 'redeem_code_submitted',
  REDEEM_CODE_VALIDATED: 'redeem_code_validated',
  REDEEM_CODE_VALIDATION_FAILED: 'redeem_code_validation_failed',
  REDEEM_EMAIL_SUBMITTED: 'redeem_email_submitted',
  REDEEM_AUTH_METHOD_VIEWED: 'redeem_auth_method_viewed',
  REDEEM_AUTH_METHOD_SWITCHED: 'redeem_auth_method_switched',
  REDEEM_PASSWORD_SUBMITTED: 'redeem_password_submitted',
  REDEEM_REGISTER_SUCCEEDED: 'redeem_register_succeeded',
  REDEEM_REGISTER_FAILED: 'redeem_register_failed',
  REDEEM_OTP_SENT: 'redeem_otp_sent',
  REDEEM_OTP_SEND_FAILED: 'redeem_otp_send_failed',
  REDEEM_OTP_RESENT: 'redeem_otp_resent',
  REDEEM_OTP_VERIFIED: 'redeem_otp_verified',
  REDEEM_OTP_VERIFY_FAILED: 'redeem_otp_verify_failed',
  REDEEM_CODE_APPLIED: 'redeem_code_applied',
  REDEEM_APPLY_FAILED: 'redeem_apply_failed',
  REDEEM_SUCCESS_VIEWED: 'redeem_success_viewed',
  REDEEM_FLOW_COMPLETED: 'redeem_flow_completed',
  REDEEM_BACK_CLICKED: 'redeem_back_clicked',

  // ── TV Device Pairing (/tv) ──
  // Phone/web side of the TV login handshake: the TV app shows a code, the
  // user confirms it here. `tv_` domain (§7). Properties: { reason } on
  // failures (not_found | expired | already_confirmed | subscription_required
  // | request_failed).
  TV_PAIRING_CONFIRMED: 'tv_pairing_confirmed',
  TV_PAIRING_CONFIRM_FAILED: 'tv_pairing_confirm_failed',
  TV_PAIRING_CANCELLED: 'tv_pairing_cancelled',

  // ── App Store Rating Prompt (/rate) ──
  // App-store review nudge — distinct from in-app content ratings.
  // `app_rating_` domain (§7). Properties: { store, is_authenticated }.
  APP_RATING_STORE_CLICKED: 'app_rating_store_clicked',

  // ── Home feed rows ──
  // `home_` domain (§7). Segmented tabs on a pinned home row (FRONT-398).
  // Properties: { row: 'only_on_taboo', tab: 'all' | 'sensitive' }.
  HOME_ROW_TAB_CHANGED: 'home_row_tab_changed',
} as const);

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];
